import { DatePickerComponent } from "@syncfusion/ej2-react-calendars";
import {
  DropDownButtonComponent,
  ItemModel,
} from "@syncfusion/ej2-react-splitbuttons";
import { enableRipple } from "@syncfusion/ej2-base";
import React, { useEffect, useMemo, useRef } from "react";
import {
  getHistoricalData,
  getHistoricalDataReturnItemType,
  getHistoricalDataReturnType,
} from "../api";
import {
  Category,
  ChartComponent,
  DataLabel,
  LineSeries,
  Legend,
  Tooltip,
  Inject,
  SeriesCollectionDirective,
  SeriesDirective,
  DateTimeCategory,
  Export,
} from "@syncfusion/ej2-react-charts";
import { IBaseComponent } from "./types";
import { useSnackbar } from "notistack";
import moment from "moment";
import { useSelector } from "react-redux";
import { Redirect } from "react-router-dom";
import { RootState } from "../store";
import { CommandColumnRenderer } from "@syncfusion/ej2-react-grids";
import Select from "react-select";
import { useAppSelector } from "../util/hooks";
import { AxiosResponse } from "axios";

export interface IGraphReportComponent extends IBaseComponent {
  SelectedDataParam: string | undefined;
  setSelectedDataParam: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  rowIndexValue: React.MutableRefObject<number>;
}
enableRipple(true);
export const GraphReport: React.FC<IGraphReportComponent> = ({
  Devices,
  SelectedDevice,
  setSelectedDevice,
  StartDate,
  setStartDate,
  EndDate,
  setEndDate,
  AliveData,
  Data,
  SelectedDataParam,
  setSelectedDataParam,
  TotalData,
  rowIndexValue,
  setData,
  setTotalData,
  setHistoricLoading,
  setSelectedDataParameter,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const unused_parameters = {
    id: null,
    dev_id: null,
    timestamp: null,
    rssi: null,
    power_good: null,
    battery: null,
    createdAt: null,
    updatedAt: null,
    lat: null,
    long: null,
    tamper: null,
    solar_good: null,
    temperature: null,
    location: null,
    created_at: null,
  };

  const { userRole } = useAppSelector((s) => s.user);

  const DeviceDataParameters = useMemo(() => {
    if (Data.length === 0) {
      const DeviceParams: ItemModel[] = [];
      return DeviceParams;
    }
    let item: getHistoricalDataReturnItemType = Data[0];
    return Object.keys(item)
      .map(
        (i) =>
          ({
            text: i,
            id: i,
          } as ItemModel)
      )
      .filter((i) => !(!i?.id || i.id in unused_parameters));
  }, [Data]);

  const params = [
    { text: "LA(s)", id: "las" },
    { text: "LC(s)", id: "lcs" },
    { text: "LZ(s)", id: "lzs" },
    { text: "LA(eqt)", id: "laeqt" },
    { text: "LC(eqt)", id: "lceqt" },
    { text: "LZ(eqt)", id: "lzeqt" },
    { text: "LA(peakt)", id: "lapeakt" },
    { text: "LC(peakt)", id: "lcpeakt" },
    { text: "LZ(peakt)", id: "lzpeakt" },
  ];
  const params2 = [{ text: "LA(eqt)", id: "laeqt" }];

  const DeviceDropDownListItems: any[] = useMemo(
    () =>
      Devices.map((d) => {
        const item: any = {
          label: `${d.location}, Kochi`,
          value: d.dev_id,
        };
        return item;
      }),
    [Devices]
  );
  const DownloadOptions: ItemModel[] = useMemo(
    () =>
      ["JPEG", "PNG", "PDF", "SVG"].map(
        (i) =>
          ({
            text: i,
            id: i,
          } as ItemModel)
      ),
    []
  );
  const chartRef = useRef<ChartComponent | null>(null);
  const screenRef = useRef<HTMLDivElement | null>(null);
  const { state } = useSelector((state: RootState) => state.user);
  const [ParamLabel, setParamLabel] = React.useState<string>("LA(eqt)");

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      // This line disable the blue border

      border: state.isFocused ? "1px solid #acacac" : "1px solid #acacac",
      boxShadow: state.isFocused ? 0 : 0,
      "&:hover": {
        boxShadow: state.isFocused ? 0 : 0,
      },
    }),
  };

  React.useEffect(() => {
    if (chartRef.current) {
      chartRef.current.title = `ANMS ${
        DeviceDropDownListItems.find((i) => i.value === SelectedDevice?.dev_id)
          ?.label
      } ${moment(StartDate).format("MM/DD/YYYY hh:mm:ss a")} To ${moment(
        EndDate
      ).format("MM/DD/YYYY hh:mm:ss a")}`;
      chartRef.current?.refresh();
    }
  }, [SelectedDataParam, SelectedDevice, StartDate, EndDate]);

  useEffect(() => {
    const getHistoryData = async () => {
      // if (state === "guest") return;
      setHistoricLoading(true);
      // enqueueSnackbar(`Fetching Historic ANMS data.`, {
      //   variant: "info",
      // });
      const res: AxiosResponse<getHistoricalDataReturnType> =
        await getHistoricalData({
          // dev_id: SelectedDevice?.dev_id || "",
          location_id: SelectedDevice?.id || 274,
          start_time: moment(StartDate).format("YYYY-MM-DD") + " 00:00:00",
          end_time: moment(EndDate).format("YYYY-MM-DD") + " 23:59:59",
        });

      setHistoricLoading(false);

      // console.log({ res });
      if (typeof res?.data === "undefined") {
        // enqueueSnackbar(`Failed to fetch latest Historic ANMS data.`, {
        //   variant: "error",
        // });
        setData([]);
        rowIndexValue.current = 0;
        setTotalData(0);
      } else {
        // enqueueSnackbar(
        //   `Latest Historic ANMS data fetched. ${res.data.total} rows fetched.`,
        //   {
        //     variant: "success",
        //   }
        // );
        const response_data: getHistoricalDataReturnType = res.data;

        setData(
          response_data.data.map((i, index) => ({
            ...i,
            timestamp: moment(i.timestamp).format("lll"),
            id: index + 1,
            location: SelectedDevice ? SelectedDevice.location : "N/A",
          }))
        );

        setSelectedDataParameter("laeqt");
        setTotalData(response_data.total);
      }
    };
    rowIndexValue.current = 0;
    console.log("Resetting rowIndexValue");
    getHistoryData();
  }, [SelectedDevice, StartDate, EndDate, state]);

  if (state !== "loggedIn") return <Redirect to="/signin" />;

  const primaryyaxis = {
    title: ParamLabel,
  };

  const typeOfData = () => {
    if (
      SelectedDataParam === "las" ||
      SelectedDataParam === "lcs" ||
      SelectedDataParam === "lzs"
    ) {
      return [...AliveData].reverse();
    } else {
      return [...Data].reverse();
    }
  };

  return (
    <div
      className="flex flex-1 flex-col justify-start items-center w-full"
      ref={(r) => (screenRef.current = r)}
    >
      <div className="flex flex-col sm:flex-row justify-start items-center w-full gap-8 mt-3">
        <Select
          className="basic-single w-1/4 text-black ml-2"
          classNamePrefix="select"
          styles={customStyles}
          isSearchable
          name="color"
          placeholder={
            DeviceDropDownListItems.find(
              (i) => i.value === SelectedDevice?.dev_id
            )?.label
          }
          onChange={(e: any) => {
            setSelectedDevice(Devices.find((d) => d.dev_id === e.value));
          }}
          options={DeviceDropDownListItems}
        />

        <div className="w-2/12">
          <DatePickerComponent
            id="datepicker"
            value={StartDate}
            placeholder="Enter Start Date"
            onChange={(e: any) => {
              setStartDate(new Date(e.value));
            }}
            format="dd/MM/yyyy"
          />
        </div>
        <div className="w-2/12">
          <DatePickerComponent
            id="datepicker"
            placeholder="Enter End date"
            value={EndDate}
            min={StartDate}
            onChange={(e: any) => {
              setEndDate(new Date(e.value));
            }}
            format="dd/MM/yyyy"
          />
        </div>
        <DropDownButtonComponent
          id="paramsList"
          items={userRole === "admin" ? params : params2}
          select={(e: any) => {
            setSelectedDataParam(e.item.properties.id);
            setParamLabel(e.item.text);
          }}
        >
          {typeof SelectedDataParam === "undefined"
            ? "Select Data Parameter"
            : ParamLabel}
        </DropDownButtonComponent>
        <div className="flex flex-1 justify-end items-center">
          Showing {TotalData} points
        </div>
        <div className="mr-2">
          <DropDownButtonComponent
            id="downloadOptions"
            items={DownloadOptions}
            select={(e: any) => {
              console.log("Downloading ", e.item.properties.id);
              if (chartRef.current === null)
                enqueueSnackbar(`Chart is not loaded yet.`, {
                  variant: "error",
                });
              else {
                chartRef.current.exportModule.export(
                  e.item.properties.id,
                  `${SelectedDevice?.location},${
                    SelectedDevice?.street
                  }-${moment(StartDate).format("ll")}-${moment(EndDate).format(
                    "ll"
                  )}`
                );
              }
            }}
          >
            Download
          </DropDownButtonComponent>
        </div>
      </div>
      <div className="flex-1">
        {Data.length > 0 && !!SelectedDataParam && (
          <ChartComponent
            id="graph"
            ref={(c) => (chartRef.current = c)}
            primaryXAxis={{
              title: `Date-Time`,
              valueType: "DateTimeCategory",
              labelFormat: "d/M/y H:m",
              labelRotation: 270,
              isInversed: true,
            }}
            primaryYAxis={primaryyaxis}
            tooltip={{ enable: true }}
            style={{
              marginTop: 20,
            }}
            width={`${window.innerWidth}`}
          >
            <Inject
              services={[
                LineSeries,
                Legend,
                Tooltip,
                DataLabel,
                Category,
                Export,
                DateTimeCategory,
              ]}
            />
            <SeriesCollectionDirective>
              <SeriesDirective
                dataSource={typeOfData()}
                xName="timestamp"
                yName={SelectedDataParam}
                name={`${SelectedDataParam?.toUpperCase()} Data: ${
                  SelectedDevice?.location
                }, Kochi From ${moment(StartDate).format("ll")} To ${moment(
                  EndDate
                ).format("ll")}`}
                animation={{ enable: true, duration: 1200, delay: 100 }}
                type="Line"
                marker={{ visible: true, width: 5, height: 5 }}
                enableTooltip
              />
            </SeriesCollectionDirective>
          </ChartComponent>
        )}
      </div>
    </div>
  );
};
